"use client";

import {
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { EyeIcon, XIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { formatDateTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { KycVerificationRequestItem } from "../api/kyc-client";
import { useKycRequestListQuery } from "../api/kyc-queries";

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "submitted", label: "ລໍຖ້າກວດສອບ" },
  { value: "under_review", label: "ກຳລັງກວດສອບ" },
  { value: "approved", label: "ອະນຸມັດແລ້ວ" },
  { value: "rejected", label: "ປະຕິເສດ" },
] as const;

const TIER_LABELS: Record<string, string> = {
  unverified: "ບໍ່ໄດ້ຢືນຢັນ",
  basic: "ພື້ນຖານ",
  verified: "ຢືນຢັນແລ້ວ",
  premium: "ພຣີມຽມ",
};

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  submitted: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ລໍຖ້າກວດສອບ",
  },
  under_review: {
    dot: "bg-secondary",
    text: "text-secondary",
    label: "ກຳລັງກວດສອບ",
  },
  approved: {
    dot: "bg-success",
    text: "text-success",
    label: "ອະນຸມັດແລ້ວ",
  },
  rejected: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ປະຕິເສດ",
  },
};

export function KycRequestsPage() {
  const nav = useNavigate({ from: "/app/wallet/kyc-requests" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/wallet/kyc-requests",
  });

  const list = useKycRequestListQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const statusFilter = findCondition(filters, "status");
  const statusValue = (statusFilter?.value as string) || "all";
  const hasFilter = statusValue !== "all";

  function handleStatusChange(value: string) {
    let next: FilterConditionDTO[] | undefined = filters;
    if (value === "all") {
      next = removeConditions(filters, "status");
    } else {
      next = upsertCondition(filters, { field: "status", op: "eq", value });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }

  const columns: TanstackReactTable.ColumnDef<KycVerificationRequestItem>[] = [
    createSortableColumn<KycVerificationRequestItem>("createdAt", "ວັນທີ", {
      size: 170,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "customer",
      header: "ລູກຄ້າ",
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-sm">
            {row.original.fullName ?? row.original.customerName ?? "—"}
          </div>
          <div className="truncate text-muted-foreground text-xs">
            {row.original.customerEmail ?? "—"}
          </div>
        </div>
      ),
      size: 220,
    },
    {
      id: "tiers",
      header: "ລະດັບ",
      cell: ({ row }) => (
        <span className="text-sm">
          {TIER_LABELS[row.original.currentKycTier] ??
            row.original.currentKycTier}{" "}
          →{" "}
          {TIER_LABELS[row.original.requestedTier] ??
            row.original.requestedTier}
        </span>
      ),
      size: 180,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = row.original.status;
        const style = STATUS_STYLE[s] ?? {
          dot: "bg-muted-foreground",
          text: "text-muted-foreground",
          label: s,
        };
        return (
          <div className="flex items-center gap-2">
            <span
              className={`size-2 shrink-0 rounded-full ${style.dot}`}
              aria-hidden
            />
            <span className={`text-sm ${style.text}`}>{style.label}</span>
          </div>
        );
      },
      size: 140,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງລາຍລະອຽດ",
              icon: <EyeIcon className="size-4" />,
              onClick: () =>
                nav({
                  to: "/app/wallet/kyc-requests/$id",
                  params: { id: row.original.id },
                }),
            },
          ]}
        />
      ),
      size: 60,
    },
  ];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ຄຳຂໍ KYC" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SimpleSelect
            value={statusValue}
            onValueChange={handleStatusChange}
            options={STATUS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            className="w-[200px]"
          />
          {hasFilter ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
              onClick={() => handleStatusChange("all")}
            >
              <XIcon className="size-3.5" />
              ລ້າງຕົວກອງ
            </button>
          ) : null}
        </div>

        <DataTable
          columns={columns}
          data={list.data?.data ?? []}
          isLoading={list.isLoading}
          pagination={{
            total: list.data?.meta.total ?? 0,
            limit: search.limit ?? 20,
            offset: search.offset ?? 0,
            onPageChange: (offset) => nav({ search: { ...search, offset } }),
          }}
        />
      </Main>
    </>
  );
}
